import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useState } from "react";
import SandpackWithEditor from "./SandpackWithEditor";
import SandpackWithTests from "./SandpackWithTests";
import { SandpackFiles } from "@codesandbox/sandpack-react";
import {
  calculateRate,
  IProjectUserData,
  ISandpackProjectLog,
  ITestResult,
  ITestStatus,
  spOptions,
} from "./statics";
import { MarkdownRenderer } from "../../../components/MyMarkdown";
import { filterFilesByKeys, myAxios } from "../../../utils/common";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft } from "@phosphor-icons/react";
import { Link } from "react-router";
import { IUser } from "../../../contexts/AuthContextProvider";

export default function SandpackProjectMain({
  projectData,
  user,
}: {
  projectData: IProjectUserData;
  user: IUser;
}) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [testResults, setTestResults] = useState<ITestStatus>({
    status: "IDLE",
    results: null,
  });

  const initialFiles = projectData.user_files
    ? { ...projectData.project.files, ...projectData.user_files }
    : projectData.project.files;

  const [files, setFiles] = useState(initialFiles as unknown as SandpackFiles);

  const handleSetFiles = (files: SandpackFiles) => {
    setTestResults((prev) => ({ ...prev, status: "RUNNING" }));
    setFiles(files);
  };

  const saveMutation = useMutation({
    mutationFn: async (data: ISandpackProjectLog) =>
      myAxios.post("/projectLog/", data).then((r) => r.data),
    onSuccess: (variables) => {
      if (!variables.is_auto) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 1500);
      }
    },
  });

  const resetProject = () => {
    if (
      confirm(
        "Proje dosyalarını başlangıç haline getirmek istediğine emin misin?"
      )
    ) {
      handleSetFiles(projectData.project.files as unknown as SandpackFiles);
    }
  };

  const handleSave = ({
    isAuto = false,
    currentFiles,
    results,
  }: {
    isAuto?: boolean;
    currentFiles?: SandpackFiles;
    results?: ITestResult[];
  }) => {
    const filesToUse = currentFiles ?? files;
    const userFilesToSave = filterFilesByKeys(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filesToUse as any,
      projectData.project.env_configs.filesToSave
    );
    // setTestResults henüz uygulanmamış olabilir; test sonucu geldiyse onu kullan
    const score = calculateRate(results ?? testResults.results);

    const pLogData = {
      is_auto: isAuto,
      project_id: projectData.project.id,
      user_files: userFilesToSave,
      user_id: user.id,
      user_score: score === null ? -1 : score,
    };

    saveMutation.mutate(pLogData);
  };

  return (
    <div className="w-full h-screen flex">
      <div className="border-r border-white/[0.06] w-108 overflow-hidden h-full flex flex-col">
        <div className="border-b border-white/[0.06] py-2 pr-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer text-white/50 hover:text-white transition-colors p-2 text-sm"
          >
            <ArrowLeft size="18" />
            <span>Ana sayfaya dön</span>
          </Link>
          <span className="text-sm text-white/40 font-medium">
            {user.shortName}
          </span>
        </div>
        <div className="prTask-desc p-6 pt-8 flex-1 overflow-auto">
          <h1 className="text-2xl text-myGreen font-semibold mb-4">
            {projectData.project.infos.title}
          </h1>
          <MarkdownRenderer content={projectData.project.infos.description} />
        </div>
      </div>
      <div className="flex-1 p-3 sandpack-project-main">
        <PanelGroup
          direction="vertical"
          id="whole"
          className="border-white/[0.08] border rounded-xl overflow-hidden"
        >
          <Panel>
            <SandpackWithEditor
              files={files}
              setFiles={handleSetFiles}
              spOptionz={{
                ...spOptions,
                ...projectData.project?.env_configs?.spOptions,
              }}
              testResults={testResults}
              handleReset={resetProject}
              handleSave={(currentFiles) =>
                handleSave({ isAuto: false, currentFiles })
              }
              showSuccess={showSuccess}
            />
          </Panel>

          <div className="group py-1 bg-surface-2 relative">
            <PanelResizeHandle className="h-0.5 w-full resizer" />
            <div className="resizer-ind w-7 h-2 top-[1px] left-1/2 -translate-x-1/2" />
          </div>

          <Panel defaultSize={4.5}>
            <SandpackWithTests
              files={files}
              setTestResults={setTestResults}
              handleAutoSave={(results) => handleSave({ isAuto: true, results })}
              spOptionz={{
                ...spOptions,
                ...projectData.project?.env_configs?.spOptions,
              }}
            />
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
}
